import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, Plus, Pencil, Trash2, BarChart3, PieChart, LineChart, Table, Clock, Play, Filter } from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
const API = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { value: 'sales', label: 'Sales', icon: BarChart3, color: 'bg-blue-100 text-blue-700' },
  { value: 'marketing', label: 'Marketing', icon: PieChart, color: 'bg-purple-100 text-purple-700' },
  { value: 'customer', label: 'Customer', icon: LineChart, color: 'bg-green-100 text-green-700' },
  { value: 'operations', label: 'Operations', icon: Table, color: 'bg-amber-100 text-amber-700' },
  { value: 'leads', label: 'Leads', icon: BarChart3, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'finance', label: 'Finance', icon: PieChart, color: 'bg-rose-100 text-rose-700' },
  { value: 'custom', label: 'Custom', icon: FileText, color: 'bg-gray-100 text-gray-700' },
];
const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-600' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'archived', label: 'Archived', color: 'bg-red-100 text-red-600' },
];
const SCHEDULES = ['none', 'daily', 'weekly', 'monthly'];

function ReportModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ name: '', report_type: 'custom', description: '', status: 'draft', schedule: 'none', recipients: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ name: editing.name || '', report_type: editing.report_type || 'custom', description: editing.description || '', status: editing.status || 'draft', schedule: editing.schedule || 'none', recipients: (editing.recipients || []).join(', ') });
    else setForm({ name: '', report_type: 'custom', description: '', status: 'draft', schedule: 'none', recipients: '' });
  }, [editing, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Report name is required'); return; }
    setSaving(true);
    const payload = { ...form, recipients: form.recipients.split(',').map(r => r.trim()).filter(Boolean) };
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/platform/reports/${editing.id}`, payload)
        : await axios.post(`${API}/api/platform/reports`, payload);
      toast.success(editing ? 'Report updated' : 'Report created');
      onSaved(data, !!editing);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-cyan-500" />{editing ? 'Edit' : 'New'} Report</DialogTitle>
          <DialogDescription>Configure a custom report</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Report Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="report-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={form.report_type} onValueChange={v => setForm(p => ({ ...p, report_type: v }))}>
                <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REPORT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="report-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="What this report covers..." data-testid="report-desc-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Schedule</Label>
              <Select value={form.schedule} onValueChange={v => setForm(p => ({ ...p, schedule: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SCHEDULES.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Recipients (emails)</Label><Input value={form.recipients} onChange={e => setForm(p => ({ ...p, recipients: e.target.value }))} placeholder="a@b.com, c@d.com" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-report-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterType, setFilterType] = useState('all');

  const fetchReports = useCallback(async () => {
    try {
      const params = filterType !== 'all' ? { report_type: filterType } : {};
      const { data } = await axios.get(`${API}/api/platform/reports`, { params,  });
      setReports(data);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const onSaved = (r, isUpdate) => {
    if (isUpdate) setReports(p => p.map(x => x.id === r.id ? r : x));
    else setReports(p => [r, ...p]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(`${API}/api/platform/reports/${id}`);
      setReports(p => p.filter(r => r.id !== id));
      toast.success('Report deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const getTypeMeta = (type) => REPORT_TYPES.find(t => t.value === type) || REPORT_TYPES[6];
  const getStatusMeta = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="custom-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center"><FileText className="h-5 w-5 text-cyan-500" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Custom Reports<PageInfoTooltip pageKey="custom-reports" /></h1>
            <p className="text-xs text-gray-400">{reports.length} report{reports.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-gray-400" />
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-32 h-9 text-sm" data-testid="report-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {REPORT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-cyan-600 hover:bg-cyan-700 h-9 gap-1.5 ml-auto" data-testid="new-report-btn">
          <Plus className="h-4 w-4" /> New Report
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" /></div>
      ) : reports.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No reports yet</h3>
            <p className="text-sm text-gray-400 mb-4">Create custom reports to track your business metrics</p>
            <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-cyan-600 hover:bg-cyan-700 gap-1.5">
              <Plus className="h-4 w-4" /> Create Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {reports.map(r => {
            const typeMeta = getTypeMeta(r.report_type);
            const statusMeta = getStatusMeta(r.status);
            const TypeIcon = typeMeta.icon;
            return (
              <Card key={r.id} className="border-gray-200 hover:shadow-sm transition-shadow" data-testid={`report-row-${r.id}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg ${typeMeta.color} flex items-center justify-center flex-shrink-0`}>
                    <TypeIcon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{r.name}</h3>
                    <p className="text-[10px] text-gray-400 truncate">{r.description || 'No description'}</p>
                  </div>
                  <Badge className={`${typeMeta.color} text-[9px]`}>{typeMeta.label}</Badge>
                  <Badge className={`${statusMeta.color} text-[9px]`}>{statusMeta.label}</Badge>
                  {r.schedule && r.schedule !== 'none' && (
                    <Badge variant="outline" className="text-[9px] gap-0.5"><Clock className="h-2.5 w-2.5" />{r.schedule}</Badge>
                  )}
                  <div className="text-right text-[10px] text-gray-400 w-14">
                    <p>{r.run_count || 0} runs</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(r); setModalOpen(true); }} data-testid={`edit-report-${r.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-gray-400" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(r.id)} data-testid={`delete-report-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ReportModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={onSaved} editing={editing} />
    </div>
  );
}
